import { getParams, isCYOMode, isShopMode } from '@/utils/params';

export enum TrackingFeatureTypes {
  MOVEMENT = 'movement',
  ACTION = 'action',
  EVENT = 'event',
}

interface Satellite {
  track: (eventName: string, options?: Record<string, any>) => void;
}

declare global {
  interface Window {
    _satellite: Satellite;
    trackingData: Record<string, unknown>;
  }
}

const MAX_TRIES = 10;
let tries = 0;
let tracker: Satellite;
const trackedFeatures = new Set<string>();

function isObject(item: unknown) {
  return item && typeof item === 'object' && !Array.isArray(item);
}

function mergeDeep(target: Record<any, any>, source: Record<any, any>) {
  Object.entries(source).forEach((entry) => {
    const key = entry[0];
    const value = entry[1];
    if (isObject(value)) {
      if (!target[key]) {
        Object.assign(target, {
          [key]: {},
        });
      }
      mergeDeep(target[key], value);
    } else {
      Object.assign(target, {
        [key]: value,
      });
    }
  });
}

export function init(data: Record<string, any>) {
  const adobeLaunchSrc = isShopMode()
    ? getParams().adobeLaunchSpreadshop
    : getParams().adobeLaunchD2C;

  window.trackingData = window.trackingData || {};
  mergeDeep(window.trackingData, data);

  if (adobeLaunchSrc) {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = adobeLaunchSrc;
    script.id = 'adobeLaunch';
    script.async = true;
    document.head.appendChild(script);

    waitForSatellite();
  } else {
    window._satellite = {
      track: function (event, data) {
        console.groupCollapsed('%c tracking event', 'color: grey', event);
        console.log(`event:`, event);
        console.log('event data: ', data);
        console.log('trackingData: ', window.trackingData);
        console.groupEnd();
      },
    };
    waitForSatellite();
  }
}

function waitForSatellite() {
  tries += 1;
  const satellite = window._satellite;
  if (satellite && satellite.track && typeof satellite.track === 'function') {
    tracker = satellite;
  } else if (tries >= 0 && tries <= MAX_TRIES) {
    setTimeout(waitForSatellite, 200);
  }
}

function sendAnalyticsPostMessage(eventName: string, eventDetails?: Record<string, any>) {
  if (!window.parent || window.parent === window) {
    return;
  }

  let parentOrigin: string;
  try {
    parentOrigin = new URL(document.referrer).origin;
  } catch {
    parentOrigin = '*';
  }

  window.parent.postMessage(
    {
      eventType: 'somatEvent',
      eventName,
      eventPayload: eventDetails,
      trackingData: window.trackingData,
    },
    parentOrigin
  );
}

function track(eventName: string, stepName: string, stepDetail: Record<string, any> = {}) {
  window.trackingData = window.trackingData || {};
  window.trackingData.stepName = stepName;
  window.trackingData.stepDetail = stepDetail;

  if (tracker) {
    tracker.track(eventName);
  }
}

export function trackStep(stepName: string, stepDetail: Record<string, any> = {}) {
  track('designer-step', stepName, stepDetail);
  sendAnalyticsPostMessage(stepName, stepDetail);
}

export function trackFeature(
  featureName: string,
  featureDetail?: Record<string, any>,
  trackMultipleTimes = false,
  type: TrackingFeatureTypes = TrackingFeatureTypes.ACTION
) {
  if (!trackedFeatures.has(featureName)) {
    if (!trackMultipleTimes) {
      trackedFeatures.add(featureName);
    }
    if (tracker) {
      tracker.track('designer-feature', { featureName, featureDetail, type });
    }
    sendAnalyticsPostMessage(featureName, featureDetail);
  }
}

export function updateProduct(product: {
  productTypeId: string;
  appearanceId: string;
  price: number;
}) {
  if (window.trackingData) {
    mergeDeep(window.trackingData, { products: [product] });
  }
}

export function getDesignerType() {
  let designerType;
  if (isCYOMode()) {
    designerType = 'CYO';
  }
  if (isShopMode()) {
    designerType = 'MDS';
  }
  return designerType;
}
